/**
 * Separable Gaussian blur with wrap-around edge handling
 * (since this is for tileable pattern preprocessing).
 *
 * sigma = blurRadius / 2.5; kernel size = 2 * ceil(3 * sigma) + 1.
 */

function gaussianKernel1D(sigma: number): Float32Array {
  const radius = Math.ceil(3 * sigma);
  const size = 2 * radius + 1;
  const kernel = new Float32Array(size);
  let sum = 0;
  for (let i = 0; i < size; i++) {
    const x = i - radius;
    const v = Math.exp(-(x * x) / (2 * sigma * sigma));
    kernel[i] = v;
    sum += v;
  }
  for (let i = 0; i < size; i++) {
    kernel[i] = kernel[i]! / sum;
  }
  return kernel;
}

export function gaussianBlur(source: ImageData, blurRadius: number): ImageData {
  const sigma = blurRadius / 2.5;
  if (sigma <= 0) return source;
  const kernel = gaussianKernel1D(sigma);
  const radius = (kernel.length - 1) / 2;
  const w = source.width;
  const h = source.height;
  const tmp = new Float32Array(w * h);
  const out = new ImageData(w, h);
  const src = source.data;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0;
      for (let k = 0; k < kernel.length; k++) {
        const sx = (((x + k - radius) % w) + w) % w;
        sum += (src[(y * w + sx) * 4] ?? 0) * kernel[k]!;
      }
      tmp[y * w + x] = sum;
    }
  }

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0;
      for (let k = 0; k < kernel.length; k++) {
        const sy = (((y + k - radius) % h) + h) % h;
        sum += (tmp[sy * w + x] ?? 0) * kernel[k]!;
      }
      const val = Math.round(sum);
      const i = (y * w + x) * 4;
      out.data[i] = val;
      out.data[i + 1] = val;
      out.data[i + 2] = val;
      out.data[i + 3] = 255;
    }
  }

  return out;
}
